'use client';
import { useEffect, useRef, useState } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Box,
  Typography,
  Popper,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { MyLocation, LocationOn, Clear } from '@mui/icons-material';

// Google Places Autocomplete with fallback to browser geolocation
export default function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelected,
  label = 'Address',
  placeholder = '',
  sx = {},
  clearable = false,
  size = 'medium',
  fullWidth = false,
}) {
  const inputRef = useRef(null);
  const [hasGoogleMaps, setHasGoogleMaps] = useState(false);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    // Check if Google Maps script is already loaded globally (from layout.js)
    if (typeof window !== 'undefined' && window.google && window.google.maps) {
      // Google Maps already loaded
      setHasGoogleMaps(true);
      return;
    }

    // Otherwise try to load it
    const loadScript = (url) => {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src*="maps.googleapis.com"]`);
        if (existing) return resolve(existing);
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => resolve(script);
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    const key = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
    if (!key) {
      console.warn(
        '[AddressAutocomplete] No Google Maps API key found. Using fallback geolocation.'
      );
      return;
    }

    const url = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    loadScript(url)
      .then(() => {
        setHasGoogleMaps(true);
      })
      .catch((err) => {
        console.error('[AddressAutocomplete] Google Maps script failed to load:', err);
      });
  }, []);

  // Use AutocompleteService + PlacesService and render our own dropdown so we can control appearance
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const debounceRef = useRef(null);

  const fetchPredictions = (input) => {
    if (!input || input.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      // Maps not ready yet
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoadingSuggestions(true);
    const service = new window.google.maps.places.AutocompleteService();

    service.getPlacePredictions(
      { input, componentRestrictions: { country: 'in' }, types: ['geocode'] },
      (predictions, status) => {
        setLoadingSuggestions(false);
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
          setSuggestions([]);
          setShowSuggestions(false);
          return;
        }

        // Map predictions to minimal shape we need
        const mapped = predictions.slice(0, 6).map((p) => ({
          description: p.description,
          placeId: p.place_id,
        }));

        setSuggestions(mapped);
        setShowSuggestions(mapped.length > 0);
      }
    );
  };

  const handleInputChange = (val) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(val), 300);
  };

  const selectSuggestion = (suggestion) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) return;
    const placesService = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );
    placesService.getDetails({ placeId: suggestion.placeId }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();
        const formatted = place.formatted_address || suggestion.description;

        let city = '';
        let state = '';
        let pincode = '';

        place.address_components?.forEach((component) => {
          const types = component.types;
          if (types.includes('locality')) city = component.long_name;
          if (types.includes('administrative_area_level_1')) state = component.long_name;
          if (types.includes('postal_code')) pincode = component.long_name;
        });

        if (onChange) onChange(formatted);
        if (onPlaceSelected)
          onPlaceSelected({
            fullAddress: formatted,
            latitude: lat,
            longitude: lng,
            city,
            state,
            pincode,
            place,
          });
      }
      setShowSuggestions(false);
    });
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCoords({ lat, lng });

        // Reverse geocode if Google Maps available
        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const place = results[0];
              const formatted = place.formatted_address;

              let city = '';
              let state = '';
              let pincode = '';

              place.address_components?.forEach((component) => {
                const types = component.types;
                if (types.includes('locality')) city = component.long_name;
                if (types.includes('administrative_area_level_1')) state = component.long_name;
                if (types.includes('postal_code')) pincode = component.long_name;
              });

              if (onChange) onChange(formatted);
              if (onPlaceSelected) {
                onPlaceSelected({
                  fullAddress: formatted,
                  latitude: lat,
                  longitude: lng,
                  city,
                  state,
                  pincode,
                });
              }
            }
          });
        } else {
          // Fallback: just set coordinates
          if (onPlaceSelected) {
            onPlaceSelected({
              fullAddress: value || `Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              latitude: lat,
              longitude: lng,
              city: '',
              state: '',
              pincode: '',
            });
          }
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Unable to get your location. Please enter address manually.');
      }
    );
  };

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <TextField
        size={size}
        fullWidth={fullWidth}
        sx={sx}
        label={label}
        placeholder={placeholder}
        inputRef={inputRef}
        value={value}
        onChange={(e) => {
          const val = e.target.value;
          if (onChange) onChange(val);
          handleInputChange(val);
          setAnchorEl(inputRef.current);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setShowSuggestions(true);
          setAnchorEl(inputRef.current);
        }}
        autoComplete="off"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {clearable && value && (
                <IconButton
                  onClick={() => {
                    if (onChange) onChange('');
                    if (onPlaceSelected) onPlaceSelected(null);
                  }}
                  size="small"
                  edge="end"
                  aria-label="clear"
                >
                  <Clear fontSize="small" />
                </IconButton>
              )}
              <IconButton onClick={handleGetCurrentLocation} size="small" edge="end">
                <MyLocation fontSize="small" color="primary" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Suggestions popper (custom-rendered) */}
      <Popper
        open={showSuggestions}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 1400 }}
      >
        <Paper
          sx={{
            width: inputRef.current ? inputRef.current.offsetWidth : 320,
            boxShadow: 3,
            mt: 1,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {loadingSuggestions ? (
            <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} /> <Typography variant="body2">Searching...</Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {suggestions.map((s) => {
                const [primary, ...rest] = s.description.split(',');
                const secondary = rest.join(', ').trim();
                return (
                  <ListItemButton
                    key={s.placeId}
                    onClick={() => selectSuggestion(s)}
                    sx={{
                      py: 0.5,
                      px: 2,
                      borderBottom: '1px solid rgba(0,0,0,0.04)',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={700}>
                          {primary}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {secondary}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                );
              })}
              {/* Attribution (required by Google when using Places API). Keep subtle. */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  textAlign: 'right',
                  background: 'rgba(0,0,0,0.02)',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Powered by Google
                </Typography>
              </Box>
            </List>
          )}
        </Paper>
      </Popper>

      {coords && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Chip
            icon={<LocationOn />}
            label={`${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`}
            size="small"
            color="success"
            variant="outlined"
          />
        </Stack>
      )}
    </Box>
  );
}
